import { Controller } from 'stimulus';

import { RELATIVE_RISK_LABELS } from '../models/constants';
import { parseInteger } from '../models/helpers';

export default class extends Controller {
  static targets = [
    'slider',
    'portfolioDisplay',
    'overallRisk',
    'autoglideToggle',
    'autoglideToggleInput',
    'autoglideToggleLabel',
    'submitButton',
    'recommendedCopy',
  ];

  sliderTarget: HTMLInputElement;

  portfolioDisplayTarget: HTMLElement;

  overallRiskTarget: HTMLElement;

  autoglideToggleTarget: HTMLElement;

  autoglideToggleInputTarget: HTMLInputElement;

  autoglideToggleLabelTarget: HTMLLabelElement;

  submitButtonTarget: HTMLButtonElement;

  recommendedCopyTarget: HTMLElement;

  connect() {
    this.updatePortfolioDisplayLabel();
    this.updateOverallRiskLabel();
    this.changeSubmitButtonText();
    if (this.autoglideEligible) {
      this.updateAutoglideCopy();
    }
  }

  sliderDragged() {
    this.updatePortfolioDisplayLabel();
    this.updateOverallRiskLabel();
    this.changeSubmitButtonText();
    if (this.autoglideEligible) {
      this.turnOffAutoglide();
    }
  }

  autoglideToggled() {
    if (this.autoglideToggleInputTarget.checked) {
      this.sliderTarget.value = `${this.recommendedPosition}`;
      this.updatePortfolioDisplayLabel();
      this.updateOverallRiskLabel();
    }
    this.updateAutoglideCopy();
  }

  changeSubmitButtonText() {
    if (this.finalStep) {
      this.submitButtonTarget.value = 'Submit';
      this.submitButtonTarget.dataset.disableWith = 'Submitting...';
    } else {
      this.submitButtonTarget.value = 'Review Tax Impact';
      this.submitButtonTarget.dataset.disableWith = 'Continuing...';
    }
  }

  updatePortfolioDisplayLabel() {
    this.portfolioDisplayTarget.textContent = this.portfolioDisplayMetadata[this.sliderPosition];
  }

  updateOverallRiskLabel() {
    this.overallRiskTarget.textContent = this.overallRiskLabel();
    this.element.className = this.allocationSelectorClass();
  }

  turnOffAutoglide() {
    if (this.autoglideToggleInputTarget.checked) {
      this.autoglideToggleInputTarget.checked = false;
      this.updateAutoglideCopy();
    }
  }

  updateAutoglideCopy() {
    if (this.autoglideToggleInputTarget.checked) {
      this.autoglideToggleTarget.classList.remove('is-disabled');
      this.recommendedCopyTarget.classList.add('u-displayNone');
      this.autoglideToggleLabelTarget.textContent = 'Auto-adjust: On';
    } else {
      this.autoglideToggleTarget.classList.add('is-disabled');
      this.recommendedCopyTarget.classList.remove('u-displayNone');
      this.autoglideToggleLabelTarget.textContent = 'Auto-adjust: Off';
    }
  }

  overallRiskLabel() {
    if (this.sliderPercent < this.lowerBound) {
      return RELATIVE_RISK_LABELS.tooConservative;
    }
    if (this.sliderPercent < this.moderateBound) {
      return RELATIVE_RISK_LABELS.conservative;
    }
    if (this.sliderPercent <= this.aggressiveBound) {
      return RELATIVE_RISK_LABELS.moderate;
    }
    if (this.sliderPercent <= this.upperBound) {
      return RELATIVE_RISK_LABELS.aggressive;
    }
    return RELATIVE_RISK_LABELS.tooAggressive;
  }

  allocationSelectorClass() {
    if (this.sliderPercent < this.lowerBound) {
      return 'AllocationSelector AllocationSelector--veryConservative';
    }
    if (this.sliderPercent < this.moderateBound) {
      return 'AllocationSelector AllocationSelector--conservative';
    }
    if (this.sliderPercent <= this.aggressiveBound) {
      return 'AllocationSelector AllocationSelector--moderate';
    }
    if (this.sliderPercent <= this.upperBound) {
      return 'AllocationSelector AllocationSelector--aggressive';
    }
    return 'AllocationSelector AllocationSelector--veryAggressive';
  }

  get finalStep() {
    return (
      parseFloat(this.data.get('default-value')!) === this.sliderPosition ||
      this.data.get('tax-impact-eligible') === 'false'
    );
  }

  get autoglideEligible() {
    return this.data.get('autoglide-eligible') === 'true';
  }

  get recommendedRisk() {
    return parseFloat(this.data.get('recommended-risk-percent')!);
  }

  get lowerBound() {
    return parseFloat(this.data.get('lower-bound-risk-percent')!);
  }

  get moderateBound() {
    return parseFloat(this.data.get('moderate-risk-percent')!);
  }

  get aggressiveBound() {
    return parseFloat(this.data.get('aggressive-risk-percent')!);
  }

  get upperBound() {
    return parseFloat(this.data.get('upper-bound-risk-percent')!);
  }

  get sliderPercent() {
    return (this.sliderPosition / this.sliderMaximum) * 100;
  }

  get sliderPosition() {
    return parseInteger(this.sliderTarget.value);
  }

  get recommendedPosition() {
    return (this.recommendedRisk / 100.0) * this.sliderMaximum;
  }

  get sliderMaximum() {
    return parseFloat(this.data.get('slider-maximum-value')!);
  }

  get portfolioDisplayMetadata() {
    return JSON.parse(this.data.get('portfolio-display-metadata')!) as string;
  }
}
